import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchControlDepartments} from '@/entities/attendance/api';
import {moveDeviceToDepartment} from '@/entities/device/api';
import type {DeviceSummary} from '@/entities/device/types';
import type {StreamCamera} from '@/entities/stream/types';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {Card} from '@/shared/ui/card';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Отдел каждой камеры регистратора — по отдельности.
 *
 * У канала своя строка устройства (`<nvr>:<cameraId>`, nvr/cabinet.md §3), и отдел
 * пишется в неё тем же маршрутом, что у терминала. Так «Вход» и «Склад» одного
 * регистратора живут в разных отделах (владелец, 2026-10-22).
 *
 * «Как у регистратора» — канал без своего отдела: он следует за отделом коробки,
 * выбранным выше. Это и есть способ разложить шестнадцать камер быстро: коробку в
 * основной отдел, а в другие — только те, что стоят не там.
 */
const styles = StyleSheet.create({
  card: {gap: spacing(1)},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    minHeight: 44,
    flexWrap: 'wrap',
  },
  name: {flex: 1, minWidth: 140},
});

function ChannelRow({
  companyId,
  camera,
  row,
  options,
  inherited,
}: {
  companyId: string;
  camera: StreamCamera;
  row: DeviceSummary | undefined;
  options: {value: string; label: string}[];
  inherited: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const move = useMutation({
    mutationFn: (id: string) => moveDeviceToDepartment(companyId, camera.deviceId, id),
    onSuccess: () => qc.invalidateQueries({queryKey: ['devices']}),
  });
  const value = move.isPending ? move.variables ?? '' : row?.department_id ?? '';

  return (
    <View>
      <View style={styles.row}>
        <AppText variant="subtitle" numberOfLines={1} style={styles.name}>
          {camera.name || camera.deviceId}
        </AppText>
        {row ? (
          <FilterSelect
            label={t('deviceAdmin.department')}
            value={value}
            neutral=""
            options={[{value: '', label: inherited}, ...options]}
            onChange={id => move.mutate(id)}
            testID={`channel-department-${camera.deviceId}`}
          />
        ) : (
          // Канал ещё не пришёл строкой в список устройств — писать отдел некуда.
          <AppText variant="caption" color={p.mutedForeground}>
            {t('live.view.channelNoRow')}
          </AppText>
        )}
      </View>
      {move.isError ? (
        <Banner
          tone="error"
          title={
            (move.error as Error)?.message === 'department_not_applied'
              ? t('deviceAdmin.departmentNotApplied')
              : move.error instanceof ApiError
              ? move.error.message
              : t('errors.generic')
          }
        />
      ) : null}
    </View>
  );
}

export function ChannelDepartmentsPanel({
  companyId,
  channels,
  rows,
  recorder,
}: {
  companyId: string;
  channels: StreamCamera[];
  /** Список устройств компании: в нём строки каналов и самого регистратора. */
  rows: DeviceSummary[];
  recorder: DeviceSummary | undefined;
}) {
  const {t} = useTranslation();
  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId),
  });
  const options = (departments.data?.departments ?? []).map(d => ({
    value: d.department_id,
    label: d.name,
  }));
  const inherited = recorder?.department_id
    ? t('live.view.asRecorderNamed', {
        name: recorder.department_name || recorder.department_id,
      })
    : t('live.view.asRecorder');

  return (
    <Card padding={4} style={styles.card} testID="channel-departments">
      {channels.map(camera => (
        <ChannelRow
          key={camera.deviceId}
          companyId={companyId}
          camera={camera}
          row={rows.find(d => d.device_id === camera.deviceId)}
          options={options}
          inherited={inherited}
        />
      ))}
    </Card>
  );
}
